import React from 'react';
import { motion } from 'framer-motion';
import { Server, Activity, MonitorPlay, Zap, HardDrive, GitBranch } from 'lucide-react';

const VegaFlux = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const features = [
    { 
      title: "Interactive Topology Canvas", 
      desc: "Visually design and validate your multi-node HFT network with our live React Flow powered canvas.",
      icon: MonitorPlay
    },
    { 
      title: "R/W CLI Emulator", 
      desc: "Direct, real-time command-line configuration for Arista EOS and Cisco IOS virtual instances.",
      icon: Activity
    },
    { 
      title: "Virtual Colocation", 
      desc: "Deploy your entire trading infrastructure—OMS, Matching Engines, and Market Data Handlers—on virtual hardware.",
      icon: Server
    },
    { 
      title: "6-Phase ZTP Pipeline", 
      desc: "Automate lab resource allocation, firmware booting, and state-synced configuration pushes in seconds.",
      icon: Zap
    },
    { 
      title: "Hypervisor Sandbox", 
      desc: "Seamlessly bridge QEMU and Containerlab for true-to-life operating system environments.",
      icon: HardDrive
    },
    { 
      title: "Simulation-First Loop", 
      desc: "Validate multi-protocol BGP convergence & FIX sessions mathematically before physical deployment.",
      icon: GitBranch
    }
  ];

  return (
    <div className="pt-24 min-h-screen bg-transparent">
      {/* --- HERO SECTION --- */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[hsl(var(--peacock-blue))]/10 rounded-full blur-[100px] -mr-64 -mt-64"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[hsl(var(--primary-gold))]/10 rounded-full blur-[100px] -ml-64 -mb-64"></div>

        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="brand-subtitle mb-4">Full Virtual Colocation</h2>
            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              <span className="gradient-text">VEGAFLUX</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              The premier preproduction digital twin laboratory. Deploy, configure, and validate your 
              entire 10-Gigabit trading stack mathematically before physical deployment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* --- FEATURES GRID --- */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((f, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                className="glass-effect p-8 rounded-2xl border border-[hsl(var(--border))]/30 hover:border-[hsl(var(--peacock-blue))]/50 transition-all duration-300 group hover:shadow-[0_0_30px_hsla(180,70%,45%,0.1)]"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--border))]/20 flex items-center justify-center mb-6 border border-[hsl(var(--border))]/50 group-hover:neon-glow transition-all duration-300">
                  <f.icon className="w-7 h-7 text-[hsl(var(--primary-gold))]" />
                </div>
                <h3 className="text-2xl font-bold mb-4 brand-title text-white">{f.title}</h3>
                <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">
                  {f.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      
      {/* Footer Buffer */}
      <div className="py-20"></div>
    </div>
  );
};

export default VegaFlux;
